import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import DeviceService from "../../../api/deviceService";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const RegisterDeviceDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    device_identifier: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const handleClose = () => {
    if (loading) {
      return;
    }

    setFormData({ name: "", device_identifier: "" });
    setFormErrors({});
    setError(null);
    onClose();
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Nome é obrigatório";
    }

    if (!formData.device_identifier.trim()) {
      errors.device_identifier = "Identificador é obrigatório";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await DeviceService.registerDevice({
        name: formData.name.trim(),
        device_identifier: formData.device_identifier.trim(),
      });

      if (response?.data) {
        onSuccess(response.data);
        handleClose();
      }
    } catch (err) {
      console.error("Error registering device:", err);

      if (err.response?.data) {
        const errorData = err.response.data;

        if (errorData.name) {
          setFormErrors((prev) => ({ ...prev, name: errorData.name[0] }));
        }

        if (errorData.device_identifier) {
          setFormErrors((prev) => ({
            ...prev,
            device_identifier: errorData.device_identifier[0],
          }));
        }

        if (errorData.detail || errorData.non_field_errors) {
          setError(errorData.detail || errorData.non_field_errors[0]);
        }
      } else {
        setError("Erro ao registrar dispositivo. Por favor, tente novamente.");
      }

      dispatchSnackBarMessage(
        Date.now(),
        "Erro ao registrar dispositivo",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 99999999 }}
    >
      <DialogTitle>Registrar Novo Dispositivo</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Registre um novo dispositivo para permitir que ele se conecte à sua
            conta. Cada dispositivo físico deve ter um token único.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Nome do Dispositivo"
            placeholder="ex: Impressora da Cozinha"
            value={formData.name}
            onChange={handleChange("name")}
            error={Boolean(formErrors.name)}
            helperText={
              formErrors.name ||
              "Nome descritivo para identificar o dispositivo"
            }
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Identificador do Dispositivo"
            placeholder="ex: AA:BB:CC:DD:EE:FF"
            value={formData.device_identifier}
            onChange={handleChange("device_identifier")}
            error={Boolean(formErrors.device_identifier)}
            helperText={
              formErrors.device_identifier ||
              "Identificador único do hardware (MAC address, UUID, etc.)"
            }
            disabled={loading}
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            O dispositivo será registrado automaticamente para o proprietário da
            conta ({" "}
            <Typography component="span" fontWeight="medium">
              usuário mãe
            </Typography>
            ), mas ficará registrado que você o criou.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          Registrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterDeviceDialog;
